import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard, ShoppingBag } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Navigation links - "Startseite" entfernt (Logo ist Home-Link)
  const navLinks = [
    { href: '/antrag', label: 'Meine Pflegebox', icon: <ShoppingBag className="w-4 h-4" /> },
    { href: '/pflegegrad-rechner', label: 'Pflegegrad-Rechner' },
    { href: '/anspruch-pruefen', label: 'Anspruch prüfen' },
    { href: '/ratgeber', label: 'Ratgeber' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Main Navbar - kompakter ohne Top-Bar */}
      <nav className="fixed top-[56px] xl:top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - klickbar als Home-Link */}
            <Link 
              to="/" 
              className="flex items-baseline gap-2 hover:opacity-80 transition-opacity" 
              data-testid="logo-link"
              aria-label="Zur Startseite"
            >
              <span className="text-xl font-bold text-foreground font-[Manrope]">
                Medical
              </span>
              <span 
                className="text-4xl text-primary leading-none" 
                style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
              >
                π
              </span>
            </Link>

            {/* Desktop Navigation - einzeilig, kompakt */}
            <div className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    isActive(link.href) 
                      ? 'bg-primary/10 text-primary' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`nav-${link.href.replace('/', '')}`}
                >
                  {link.icon}
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right Side - Auth & CTA */}
            <div className="flex items-center gap-2">
              {/* Mobile (< xl): "Meine Pflegebox" – auf Handy als prominenter CTA neben dem Hamburger */}
              <Link
                to="/antrag"
                className="xl:hidden inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-full hover:bg-primary/90 transition-all whitespace-nowrap shadow-md"
                data-testid="nav-meine-pflegebox-mobile"
                aria-label="Zur Pflegebox"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                Meine Pflegebox
              </Link>

              {/* Desktop (≥ xl): "Anmelden" – für nicht eingeloggte Nutzer */}
              {!user && (
                <Link
                  to="/login"
                  className="hidden xl:inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-full hover:bg-primary/90 transition-all whitespace-nowrap shadow-md"
                  data-testid="nav-login-btn"
                  aria-label="Anmelden"
                >
                  <User className="w-4 h-4" />
                  Anmelden
                </Link>
              )}

              {/* Desktop Auth */}
              <div className="hidden xl:flex items-center gap-2">
                {user ? (
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all"
                        data-testid="nav-admin"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        Admin
                      </Link>
                    )}
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-all"
                    >
                      <User className="w-4 h-4" />
                      {user.first_name || 'Konto'}
                    </Link>
                    <button
                      onClick={logout}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </button>
                  </div>
                ) : (
                  null
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors relative z-10"
                aria-label="Menü öffnen"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-foreground" />
                ) : (
                  <Menu className="w-6 h-6 text-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div 
          className="xl:hidden fixed inset-x-0 top-16 bottom-0 bg-white z-40 overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                  isActive(link.href)
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-muted/50'
                }`}
              >
                {link.icon}
                {link.label}
              </Link>
            ))}

            <div className="border-t border-border my-4" />

            {user ? (
              <>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-foreground hover:bg-muted/50 transition-all"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    Admin-Dashboard
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-foreground hover:bg-muted/50 transition-all"
                >
                  <User className="w-5 h-5" />
                  Mein Konto
                </Link>
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-red-600 hover:bg-red-50 transition-all w-full text-left"
                >
                  <LogOut className="w-5 h-5" />
                  Abmelden
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-foreground hover:bg-muted/50 transition-all"
                data-testid="mobile-menu-login"
              >
                <User className="w-5 h-5" />
                Anmelden
              </Link>
            )}

            <Link
              to="/antrag"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-4 py-3 bg-secondary text-white text-center font-semibold rounded-xl hover:bg-secondary/90 transition-all mt-4"
            >
              Meine Pflegebox
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
